import { useCallback, useState } from 'react';

import { captchaChallengeFrom, type CaptchaChallenge } from './turnstile';

/**
 * A form's side of the Turnstile check: whether one is showing, the token it
 * produced, and a `key` that remounts the widget once that token is spent.
 *
 * `initial` is for forms that always ask (a WhatsApp code request); the rest
 * start without one and pick it up from the first error that carries it.
 */
export function useCaptcha(initial: CaptchaChallenge | null = null) {
  const [challenge, setChallenge] = useState<CaptchaChallenge | null>(initial);
  const [token, setToken] = useState<string | null>(null);
  const [widgetKey, setWidgetKey] = useState(0);

  /** After a request went out with the token: it cannot be used again. */
  const spend = useCallback(() => {
    setToken(null);
    setWidgetKey((key) => key + 1);
  }, []);

  /** Take up the challenge an error carries. True when it carried one. */
  const absorb = useCallback((error: unknown) => {
    const next = captchaChallengeFrom(error);
    if (next) setChallenge(next);
    return next !== null;
  }, []);

  /** Shown but not yet solved: the form should not send. */
  const pending = challenge !== null && token === null;

  return { challenge, token, widgetKey, pending, setToken, spend, absorb };
}
